import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Calculator,
  ChevronLeft,
  ChevronRight,
  FileCheck,
  FileMinus,
  FileText,
  Loader2,
  Plus,
  Repeat,
  User,
  type LucideIcon,
} from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { formatCents, type CreditNote, type Estimate, type Invoice } from "../../../lib/sales";
import { getRecurringInvoices, type RecurringFields } from "../../../lib/salesRecurring";
import { Btn, EmptyState, MenuField, SearchField, Toolbar, ToolbarGap, fmtDate, listCard } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { DOC_CONFIG, KIND_I18N, type AnyDoc, type DocConfig, type DocKind } from "./config";
import { StatusBadge } from "./shared";
import { DocRowMenu } from "./DocRowMenu";

/**
 * Number, client, date, status, total - five tracks plus the actions column, the
 * same six the head and the rows both lay out, so the figures stay under their
 * own heading without either side measuring the other.
 */
const COLS = "minmax(0,2.2fr) 1.4fr 1.1fr .9fr .9fr 44px";

/** The design's page size, and the one the list endpoints already default to. */
const PAGE_SIZE = 25;

/**
 * The sidebar's icon for each document kind (see SALES_CHILDREN in crm/nav.ts),
 * reused here for the disc at the head of a row. A document has no name to
 * initialise, and initials made out of a serial number would be noise, so the
 * disc says what kind of paper this is instead.
 */
const KIND_ICON: Record<DocKind, LucideIcon> = {
  proposals: FileCheck,
  estimates: Calculator,
  invoices: FileText,
  "credit-notes": FileMinus,
};

/** The date the document is filed under: an invoice has an issue date of its
 *  own, everything else is dated by when it was drawn up. */
function docDate(d: AnyDoc): string {
  return (d as Invoice).issueDate ?? d.createdAt;
}

/**
 * One list, four pages.
 *
 * Invoices, estimates, proposals and credit notes are the same screen with a
 * different config behind it (DOC_CONFIG in ./config), which is why the columns
 * are fixed and the deadline line, the send action and the convert action all
 * appear only where the kind actually has one.
 *
 * Rendered inside SalesShell, which already draws the page head, the shared KPI
 * strip and the sub-page title - so this component starts at the toolbar.
 */
export function DocList({ kind, user }: { kind: DocKind; user: StaffUser }) {
  const t = useT();
  const cfg = DOC_CONFIG[kind];
  const [rows, setRows] = useState<AnyDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [status, setStatus] = useState<string>("all");
  // "all" or "recurring". A second axis rather than a status, because recurring
  // is not a state a document is IN - a template can be draft, sent, paid or
  // overdue and still bill again next month.
  const [scope, setScope] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const canManage = userCanPlatform(user, "manage:crm.sales");
  // The cover-letter send opens the mailbox, which answers to its own right.
  const canMail = userCanPlatform(user, "manage:crm.email");

  useEffect(() => {
    const id = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(id);
  }, [qInput]);

  // Any change to what is being asked for starts at the first page. Staying on
  // page 4 of a result set that now has one page shows an empty card and looks
  // like the filter found nothing.
  useEffect(() => {
    setPage(1);
  }, [q, status, scope, kind]);

  // The scope belongs to invoices and to nothing else. Without this, switching
  // from Invoices to Estimates keeps "recurring only" selected, the control that
  // named it is no longer rendered (`cfg.hasRecurrence` is false there), and the
  // estimates page silently lists INVOICES with no way to say why.
  useEffect(() => {
    if (!cfg.hasRecurrence) setScope("all");
  }, [cfg.hasRecurrence, kind]);

  // Both filters are answered by the endpoint rather than by trimming the loaded
  // page: filtering client-side would silently mean "of the first twenty-five",
  // and the footer's count would then describe a different set from the rows
  // above it.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The recurring view is a DIFFERENT endpoint, not a parameter on this one.
      // "Which invoices carry a schedule" is answered by an index on
      // `recurringNextAt` and sorted by when each one is next due, which is the
      // order this view exists to show; folding it into the general list would
      // mean a second sort key and a filter the other three kinds can never use.
      const args = {
        page,
        pageSize: PAGE_SIZE,
        q: q || undefined,
        status: status === "all" ? undefined : status,
      };
      // `cfg.hasRecurrence` is re-checked here and not only in the effect above:
      // the effect runs AFTER the render that changed `kind`, so for one pass
      // `scope` is still "recurring" while `cfg` is already the new kind.
      const wantsRecurring = scope === "recurring" && cfg.hasRecurrence;
      const res = wantsRecurring ? await getRecurringInvoices(args) : await cfg.api.list(args);
      setRows(res.rows as AnyDoc[]);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [cfg, q, status, scope, page]);

  useEffect(() => {
    void load();
  }, [load]);

  // "New invoice" rather than "New": the head above says "Sales" and the four
  // document lists are otherwise the same page.
  const newLabel = t(`sales.${KIND_I18N[kind]}.new`);

  return (
    // One open menu at a time across every row.
    <PopHost>
      {/* Search and the status filter on the left, the one action pushed right.
          The status axis is a MenuField rather than PillTabs: invoices carry six
          statuses, and seven tabs next to a search field wrap onto a second row
          on any laptop. One control that names the current filter says the same
          thing in one slot. */}
      <Toolbar>
        <SearchField value={qInput} onChange={setQInput} placeholder={t("sales.search")} />
        <MenuField
          value={status}
          onChange={setStatus}
          label={t("sales.col.status")}
          options={[
            { value: "all", label: t("sales.statusAny") },
            ...cfg.statuses.map((s) => ({ value: s, label: t(`sales.status.${s}`) })),
          ]}
        />
        {cfg.hasRecurrence ? (
          <MenuField
            value={scope}
            onChange={setScope}
            label={t("sales.recurring.title")}
            options={[
              { value: "all", label: t("sales.recurring.scopeAll") },
              { value: "recurring", label: t("sales.recurring.scopeOnly") },
            ]}
          />
        ) : null}
        <ToolbarGap />
        {canManage ? (
          <Link to={`/sales/${kind}/new`} className="crm-btn crm-btn-brand">
            <Plus className="size-4" />
            {newLabel}
          </Link>
        ) : null}
      </Toolbar>

      <div className="crm-card crm-list-stack" style={listCard(COLS)}>
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={KIND_ICON[kind]}
            title={t("sales.empty")}
            body={t("sales.emptyBody")}
            action={
              canManage ? (
                <Link to={`/sales/${kind}/new`} className="crm-btn crm-btn-dark">
                  <Plus className="size-4" />
                  {newLabel}
                </Link>
              ) : null
            }
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("sales.col.number")}</span>
              <span>{t("sales.col.client")}</span>
              <span>{t("sales.col.date")}</span>
              <span>{t("sales.col.status")}</span>
              <span>{t("sales.col.total")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {rows.map((d) => (
                <DocRow
                  key={d.id}
                  doc={d}
                  cfg={cfg}
                  t={t}
                  canManage={canManage}
                  canMail={canMail}
                  onChanged={(next) => setRows((p) => p.map((x) => (x.id === next.id ? next : x)))}
                  onDeleted={() => {
                    // The last row of a page that is about to be empty: step
                    // back rather than reload into a card saying nothing was
                    // found. Page 1 has nowhere to step back to, so it reloads.
                    if (rows.length === 1 && page > 1) setPage((p) => p - 1);
                    else void load();
                  }}
                />
              ))}
            </div>

            <div className="crm-tfoot">
              {t("sales.showing", {
                from: (page - 1) * PAGE_SIZE + 1,
                to: (page - 1) * PAGE_SIZE + rows.length,
                total,
              })}
              <span className="crm-push flex gap-2.5">
                <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="size-4" />
                  {t("common.prev")}
                </Btn>
                <Btn small disabled={page * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>
                  {t("common.next")}
                  <ChevronRight className="size-4" />
                </Btn>
              </span>
            </div>
          </>
        )}
      </div>
    </PopHost>
  );
}

/**
 * What the row writes under the number: the document's own title where it has
 * one, and for a credit note the invoice it credits or the reason it was
 * written. Never the client - that has a column of its own two tracks along.
 */
function subtitle(d: AnyDoc, t: ReturnType<typeof useT>): string {
  const titled = (d as Invoice).title;
  if (titled) return titled;
  const note = d as CreditNote;
  if (note.invoice?.number) return `${t("sales.payments.invoice")} ${note.invoice.number}`;
  return note.reason ?? "-";
}

/**
 * The date the document runs out on, where the kind has one. An invoice is due,
 * a proposal or an estimate is valid until - the same slot in the row, because
 * both answer "by when".
 */
function deadline(d: AnyDoc, cfg: DocConfig, t: ReturnType<typeof useT>): string | null {
  if (cfg.hasDueDate) {
    const due = (d as Invoice).dueDate;
    return due ? t("sales.dueShort", { d: fmtDate(due) }) : null;
  }
  if (cfg.hasValidUntil) {
    const until = (d as Estimate).validUntil;
    return until ? t("sales.validShort", { d: fmtDate(until) }) : null;
  }
  return null;
}

function DocRow({
  doc,
  cfg,
  t,
  canManage,
  canMail,
  onChanged,
  onDeleted,
}: {
  doc: AnyDoc;
  cfg: DocConfig;
  t: ReturnType<typeof useT>;
  canManage: boolean;
  canMail: boolean;
  onChanged: (next: AnyDoc) => void;
  onDeleted: () => void;
}) {
  const Kind = KIND_ICON[cfg.kind];
  const Party = doc.client?.kind === "person" ? User : Building2;
  const by = deadline(doc, cfg, t);
  // Part-paid invoices only: the total column states what was billed, so an
  // invoice that has taken some money says so underneath rather than letting
  // the billed figure stand in for the received one. A fully paid invoice needs
  // no line - its status pill already says "paid".
  const paidCents = cfg.hasPayments ? (doc as Invoice).paidCents : 0;
  const partPaid = paidCents > 0 && paidCents < doc.totalCents;
  // A template, not a generated child: `recurringEveryMonths` is the one switch,
  // and a child never carries it.
  const recurs = cfg.hasRecurrence && (doc as Partial<RecurringFields>).recurringEveryMonths != null;

  return (
    // A div, not a link: the row ends in a menu button, and interactive content
    // inside an <a> is invalid HTML and swallows the button's clicks. The number
    // link stretches an ::after over the whole row instead, so the row stays
    // clickable end to end and the button still sits on top of it.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
      <span className="flex min-w-0 items-center gap-3">
        <span className="crm-av crm-av-soft" aria-hidden>
          <Kind className="size-4" strokeWidth={2} />
        </span>
        <span className="min-w-0">
          <span className="flex min-w-0 items-center gap-1.5">
            <Link
              to={`/sales/${cfg.kind}/${doc.id}`}
              className="crm-prow-n crm-tnum crm-trunc block after:absolute after:inset-0 after:rounded-full"
            >
              {doc.number}
            </Link>
            {/* A mark beside the number, not a column of its own. Six tracks
                already share this grid, and a seventh would be taken out of
                the number's width on every row - including the majority that
                have no schedule at all. */}
            {recurs ? (
              <Repeat className="crm-mut size-3.5 shrink-0" aria-label={t("sales.recurring.badge")} />
            ) : null}
          </span>
          <span className="crm-prow-s crm-trunc block">{subtitle(doc, t)}</span>
        </span>
      </span>

      <span className="crm-mut flex min-w-0 items-center gap-1.5 text-[12.5px]">
        <Party className="size-3.5 shrink-0" />
        <span className="crm-trunc">{doc.client?.name ?? "-"}</span>
      </span>

      <span className="crm-mut crm-tnum text-[12.5px]">
        {fmtDate(docDate(doc))}
        {by ? <span className="crm-prow-s crm-trunc block">{by}</span> : null}
      </span>

      <span>
        <StatusBadge status={doc.status} />
      </span>

      {/* Billed, not received. The paid figure sits under it while the two
          differ, see the note by `partPaid`. */}
      <span className="crm-tnum text-[13.25px] font-bold">
        {formatCents(doc.totalCents, doc.currency)}
        {partPaid ? (
          <span className="crm-prow-s crm-trunc block font-normal">
            {t("sales.totals.paid")} {formatCents(paidCents, doc.currency)}
          </span>
        ) : null}
      </span>

      <DocRowMenu
        doc={doc}
        cfg={cfg}
        canManage={canManage}
        canMail={canMail}
        onChanged={onChanged}
        onDeleted={onDeleted}
      />
    </div>
  );
}
